import React from 'react';
import '/public/css/components/fullTeaser.css';
import Link from 'components/AppSafeLink/AppSafeLink';
import { globals } from '../../config/globals';
import { Time, Heading } from '@apmg/titan';
import { Image } from '@apmg/mimas';
import { linkByTypeHref, linkByTypeAs, analyzeUrl } from '../../utils/cjsutils';
import { audioDownloadPrefix, getVideoEmbedUrl } from '../../utils/utils';
import AudioPlayButton from '../AudioPlayButton/AudioPlayButton';
import Contributors from 'components/Contributors/Contributors';
import IconTriangle from '../Icons/IconTriangle';
import AddFavoriteWrapper from 'components/Data/AddFavoriteWrapper';

interface CollectionLink {
  url: string;
  title: string;
  prefix?: string;
}

interface PrimaryVisuals {
  normal?: any;
  thumbnail?: any;
  video?: { url?: string } | null;
}

interface AudioEncoding {
  playFilePath?: string | null;
  durationHms?: string | null;
}

interface AudioItem {
  encodings?: AudioEncoding[];
}

interface TeaserItem {
  id: string;
  title: string;
  descriptionText?: string;
  canonicalSlug: string;
  publishDate?: string;
  resourceType?: string;
  contributors?: any[];
  primaryVisuals: PrimaryVisuals;
  collectionRelatedLinks?: CollectionLink[];
  audio?: AudioItem[];
}

interface FullTeaserProps {
  item: TeaserItem;
  size?: 'condensed' | 'normal' | string;
  newspartners?: boolean;
  loading?: 'eager' | 'lazy' | 'auto' | string;
  priority?: boolean;
  condensedBtns?: boolean;
  hideAddFavorites?: boolean;
  headingLvl?: number;
  saveButtonBG?: boolean;
}

const FullTeaser: React.FC<FullTeaserProps> = ({
  item,
  size,
  newspartners,
  loading,
  priority,
  condensedBtns,
  hideAddFavorites,
  headingLvl = 2,
  saveButtonBG,
}) => {
  // Note: link and linkAs variables are kept for potential future use
  // but are currently not used in the component

  const imageSizes = size === 'condensed' ? '120px' : globals.sizes.collection;
  const imageLoading = priority ? 'eager' : loading;
  const imageFetchPriority = priority ? 'high' : undefined;

  const imageData = item.primaryVisuals.normal
    ? item.primaryVisuals.normal
    : item.primaryVisuals.thumbnail;

  return (
    <div className="teaserContainer">
      <article className="teaser">
        {item.primaryVisuals.video ? (
          <div className="teaser-primary-visual">
            <iframe
              allowFullScreen
              loading="lazy"
              title={item.primaryVisuals.video.url}
              src={getVideoEmbedUrl(item.primaryVisuals.video.url)}
            ></iframe>
          </div>
        ) : imageData ? (
          <div className="teaser-primary-visual">
            <Link
              passHref
              data-testid="relativeLink"
              href={linkByTypeAs(item as any)}
            >
              <Image
                image={imageData}
                elementclassName="content_thumbnail"
                aspectRatio="normal"
                sizes={imageSizes}
                alt={item.title}
                loading={imageLoading as any}
                fetchPriority={imageFetchPriority as any}
              />
            </Link>
          </div>
        ) : (
          ''
        )}
        <div className="teaser_content">
          <div className="teaser_meta type-sm">
            <div className="date">
              {item.publishDate && (
                <Time
                  elementclassName="teaser_time"
                  dateTime={item.publishDate}
                  formatString="MMMM D, YYYY h:mm A"
                />
              )}
            </div>
          </div>
          <div className="teaser_header">
            <Link
              passHref
              data-testid="relativeLink"
              href={linkByTypeAs(item as any)}
            >
              <Heading level={headingLvl} className="hdg hdg-2">
                {item.title}
              </Heading>
            </Link>
          </div>
          <div className="teaser_body userContent">{item.descriptionText}</div>
          {item.collectionRelatedLinks &&
            item.collectionRelatedLinks.length > 0 && (
              <ul className="related related-teaser">
                {item.collectionRelatedLinks.map((link) => {
                  const { as } = analyzeUrl(link.url);
                  return (
                    <li
                      className="related_item"
                      key={`${link.url}${link.title}${link.prefix}`}
                    >
                      <span className="related_prefix">{link.prefix}</span>
                      <Link href={as || link.url} className="related_link">
                        {link.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          {item.contributors && item.contributors.length > 0 && (
            <div className="teaser_contributors type-sm">
              <IconTriangle
                color="var(--color-mpr-button-blue)"
                width="0.75rem"
              />
              by <Contributors authors={item.contributors} />
            </div>
          )}
          <div className="teaser-controls">
            <div className="teaser_button">
              {item?.audio?.[0]?.encodings?.[0]?.playFilePath && (
                <AudioPlayButton
                  label="Listen"
                  audioTitle={item.title}
                  audioSource={audioDownloadPrefix(
                    item.audio[0].encodings[0].playFilePath
                  )}
                  audioThumbnailSrc={
                    item.primaryVisuals.thumbnail?.aspect_ratios?.square
                      ?.instances?.[0]?.url
                  }
                  audioThumbnailAlt={
                    item.primaryVisuals.thumbnail?.shortCaption || item.title
                  }
                  showDuration={`${
                    item.audio[0]?.encodings[0]?.durationHms ?? ''
                  }`}
                  playBtnSm={condensedBtns}
                />
              )}
            </div>
            {!hideAddFavorites && (
              <div className="teaser_save">
                <AddFavoriteWrapper
                  title={item.title}
                  contentArea="mprnews"
                  resourceType={item.resourceType || ''}
                  id={item.id}
                  slug={item.canonicalSlug}
                  showBackground={saveButtonBG}
                  showIcon={true}
                />
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default FullTeaser;
